import { useState, useEffect } from 'react'
import { getClient } from '../lib/aperture'

const green = '#00ff00'
const dimGreen = 'rgba(0, 255, 0, 0.5)'
const panelBg = 'rgba(0, 255, 0, 0.05)'
const panelBorder = '1px solid rgba(0, 255, 0, 0.2)'

function StatusDot({ active }: { active: boolean }) {
  return (
    <span
      className="inline-block w-2 h-2 rounded-full mr-2"
      style={{ background: active ? green : '#ff4444', boxShadow: active ? `0 0 6px ${green}` : 'none' }}
    />
  )
}

function Row({ label, value, active }: { label: string; value: string; active?: boolean }) {
  return (
    <div className="flex items-center justify-between py-2 px-4" style={{ borderBottom: '1px solid rgba(0, 255, 0, 0.08)' }}>
      <span className="font-mono text-xs" style={{ color: dimGreen }}>{label}</span>
      <span className="font-mono text-xs flex items-center" style={{ color: green }}>
        {active !== undefined && <StatusDot active={active} />}
        {value}
      </span>
    </div>
  )
}

function SectionTitle({ title }: { title: string }) {
  return (
    <div className="font-mono text-xs font-bold uppercase tracking-wider px-4 pt-4 pb-2" style={{ color: green }}>
      {title}
    </div>
  )
}

export default function ApertureStatusTab() {
  const [sendingTest, setSendingTest] = useState(false)
  const [testSent, setTestSent] = useState(false)
  const [otelStatus, setOtelStatus] = useState<{ traces: boolean; db: boolean } | null>(null)

  const client = getClient()
  const isInitialized = !!client
  const sessionId = client?.getSessionId?.() ?? '—'
  const distinctId = client?.getDistinctId?.() ?? '—'
  const apiHost = import.meta.env.VITE_APERTURE_HOST ?? 'https://ca-aperture-dev-api.greenbay-fa050047.eastus2.azurecontainerapps.io'
  const hostDisplay = apiHost.replace(/^https?:\/\//, '')

  // Check backend OTEL status
  useEffect(() => {
    fetch('/api/health')
      .then(r => r.json())
      .then(() => setOtelStatus({ traces: true, db: true }))
      .catch(() => setOtelStatus({ traces: false, db: false }))
  }, [])

  const handleSendTest = () => {
    if (!client) return
    setSendingTest(true)
    client.capture('aperture_test_ping', { source: 'geek_mode', timestamp: new Date().toISOString() })
    setTimeout(() => {
      setSendingTest(false)
      setTestSent(true)
      setTimeout(() => setTestSent(false), 3000)
    }, 500)
  }

  return (
    <div className="flex-1 overflow-y-auto p-4">
      {/* SDK STATUS */}
      <SectionTitle title="SDK Status" />
      <div className="rounded mb-4" style={{ background: panelBg, border: panelBorder }}>
        <Row label="Initialized" value={isInitialized ? 'YES' : 'NO'} active={isInitialized} />
        <Row label="Session ID" value={sessionId} />
        <Row label="User ID" value={distinctId} active={distinctId !== '—'} />
        <Row label="Autocapture" value={isInitialized ? 'ACTIVE' : 'INACTIVE'} active={isInitialized} />
        <Row label="Session Replay Events" value={isInitialized ? 'TRANSMITTING' : 'DISABLED'} active={isInitialized} />
        <Row label="API Host" value={hostDisplay} />
      </div>

      {/* INGEST */}
      <SectionTitle title="Ingest" />
      <div className="rounded mb-2" style={{ background: panelBg, border: panelBorder }}>
        <Row label="Last ping" value={testSent ? 'Just now' : '—'} />
      </div>
      <button
        onClick={handleSendTest}
        disabled={!isInitialized || sendingTest}
        className="px-4 py-2 font-mono text-xs uppercase tracking-wider rounded mb-4 transition-all"
        style={{
          background: sendingTest ? 'rgba(0, 255, 0, 0.2)' : 'transparent',
          color: isInitialized ? green : 'rgba(0, 255, 0, 0.3)',
          border: `1px solid ${isInitialized ? 'rgba(0, 255, 0, 0.4)' : 'rgba(0, 255, 0, 0.1)'}`,
          cursor: isInitialized ? 'pointer' : 'not-allowed',
        }}
      >
        {sendingTest ? (
          <><i className="fa-solid fa-spinner fa-spin mr-2" />Sending...</>
        ) : testSent ? (
          <><i className="fa-solid fa-check mr-2" />Sent!</>
        ) : (
          'Send Test Event'
        )}
      </button>

      {/* OTEL (BACKEND) */}
      <SectionTitle title="OTEL (Backend)" />
      <div className="rounded" style={{ background: panelBg, border: panelBorder }}>
        <Row label="Traces + Metrics" value={otelStatus?.traces ? 'CONNECTED' : 'UNKNOWN'} active={otelStatus?.traces} />
        <Row label="Database (asyncpg)" value={otelStatus?.db ? 'CONNECTED' : 'UNKNOWN'} active={otelStatus?.db} />
      </div>
    </div>
  )
}
